import { type RefObject, useEffect, useRef } from 'react';

export const FOCUSABLE_SELECTOR =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

const PRESS_TO_OPEN_WINDOW_MS = 1000;

let lastPressed: { element: Element; at: number } | null = null;

const openTraps: object[] = [];

if (typeof document !== 'undefined') {
  document.addEventListener(
    'click',
    (event) => {
      const element =
        event.target instanceof Element ? event.target.closest(FOCUSABLE_SELECTOR) : null;
      lastPressed = element ? { element, at: Date.now() } : null;
    },
    true
  );
}

const takeRecentPress = (): Element | null => {
  const press = lastPressed;
  lastPressed = null;
  if (!press || Date.now() - press.at > PRESS_TO_OPEN_WINDOW_MS) return null;
  return press.element.isConnected ? press.element : null;
};

const elementToRestore = (): Element | null => {
  const recentPress = takeRecentPress();
  const active = document.activeElement;
  if (recentPress && (!active || active.contains(recentPress))) return recentPress;
  return active;
};

export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  onEscape: () => void
) {
  const trapId = useRef({});

  useEffect(() => {
    if (!active) return;
    const id = trapId.current;
    openTraps.push(id);
    return () => {
      openTraps.splice(openTraps.lastIndexOf(id), 1);
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const previous = elementToRestore();
    containerRef.current?.focus({ preventScroll: true });

    return () => {
      if (
        (previous instanceof HTMLElement || previous instanceof SVGElement) &&
        previous.isConnected
      ) {
        previous.focus();
      }
    };
  }, [active, containerRef]);

  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (openTraps[openTraps.length - 1] !== trapId.current) return;

      if (e.key === 'Escape') {
        if (!e.defaultPrevented) onEscape();
        return;
      }

      const container = containerRef.current;
      if (e.key !== 'Tab' || !container) return;

      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        e.shiftKey &&
        (document.activeElement === first || document.activeElement === container)
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, containerRef, onEscape]);
}
